import { useEffect, useState } from 'react'
import { observer } from 'mobx-react-lite'
import { store } from '../../stores/store'
import clsx from 'clsx'
import TextHeadline from '../ui/texts/TextHeadline'
import { useSteleBreakpoint } from '@/src/hooks/useBreakpoint'
import PauseScreen from './PauseScreen'
import { motion } from 'motion/react'
import { useSlideInWooshOnActive } from '@/src/game/audio/uiSounds'
import TabControls from '../ui/controls/TabControls'
import ButtonIcon from '../ui/buttons/ButtonIcon'
import IconSettings from '../ui/icons/IconSettings'
import ContinueOnPhone from './ContinueOnPhone'

const INTRO_MS = 3000
const RESULTS_MS = 5000

type GameOverPhase = 'idle' | 'intro' | 'results'

type GameOverProps = {
    onReplay: () => void
    onEditAppearance: () => void
}

//  Lose / out-of-lives overlay (character stays visible in-scene via `Game`).
const GameOver = observer(function GameOver(_props: GameOverProps) {
    const [phase, setPhase] = useState<GameOverPhase>('intro')
    const continueOnPhoneOpen = store.continueOnPhoneOpen
    const isStele = useSteleBreakpoint()

    useSlideInWooshOnActive(phase === 'intro')

    useEffect(() => {
        const introTimer = window.setTimeout(() => setPhase('intro'), INTRO_MS)
        const resultsTimer = window.setTimeout(
            () => setPhase('results'),
            INTRO_MS + RESULTS_MS,
        )

        return () => {
            window.clearTimeout(introTimer)
            window.clearTimeout(resultsTimer)
        }
    }, [])

    useEffect(() => {
        return () => store.setContinueOnPhoneOpen(false)
    }, [])

    if (continueOnPhoneOpen) {
        return (
            <ContinueOnPhone
                onClose={() => store.setContinueOnPhoneOpen(false)}
            />
        )
    }

    return (
        <div className='z-30 absolute inset-0 flex flex-col items-center px-2 pb-16 min-h-0 pointer-events-auto'>
            {phase === 'intro' ? (
                <div className='absolute inset-0 overflow-hidden pointer-events-none'>
                    <div
                        className={clsx(
                            'absolute inset-0 flex flex-col items-center',
                            'p-8',
                            'stele:p-32',
                            'animate-game-over-slide-in-top',
                        )}>
                        <motion.div
                            className={clsx(
                                'absolute flex flex-col justify-center items-center',
                                'top-8',
                                'stele:top-40'
                            )}
                        >
                            {!isStele ? (
                                <TextHeadline colorPreset='red' fontSize={48} leading={0.1}>OH NO!</TextHeadline>
                            ) : (
                                <>
                                    <TextHeadline colorPreset='red' fontSize={128} leading={0}>OH NO...</TextHeadline>
                                    <TextHeadline colorPreset='red' fontSize={128} leading={0}>Das leben kann</TextHeadline>
                                    <TextHeadline colorPreset='red' fontSize={128} leading={1}>echt hart sein!</TextHeadline>
                                </>
                            )}
                        </motion.div>
                    </div>
                    {!isStele && (
                        <>
                            <TabControls>
                                <ButtonIcon icon={<IconSettings />} />
                            </TabControls>
                            <div className='bottom-12 absolute flex justify-center items-center w-full'>
                                <div className={clsx(
                                    'flex flex-col justify-center items-center bg-white-gradient drop-shadow-2-white-stroke border border-white-stroke w-full max-w-min max-h-min pattern-background',
                                    'px-4 py-6 rounded-3xl min-w-82'
                                )}>
                                    <div className='-top-17 absolute flex flex-col justify-center items-center'>
                                        <TextHeadline fontSize={32} colorPreset='red' leading={0}>Das Leben kann</TextHeadline>
                                        <TextHeadline fontSize={32} colorPreset='red' leading={1}>echt hart sein!</TextHeadline>
                                    </div>
                                    <p className='text-white-stroke-even-darker font-nunito font-bold text-[16px] text-center leading-[120%] tracking-[-4%]'>Kein Problem! Schnapp dir nächstes mal einfach ein paar Power-Ups!</p>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            ) : null}

            {phase === 'results' ? (
                <PauseScreen variant='gameOver' />
            ) : null}
        </div>
    )
})

export default GameOver

